import { useEffect, useState } from "react";
import { seniorSchedule } from "@/data/athlete";
import { MapPin, Calendar, Clock, ChevronDown, Star } from "lucide-react";

// Parse an ISO date (YYYY-MM-DD) + display time ("7:45 PM" | "TBA") into a
// local Date. TBA times sort to end of day so the game still counts that date.
const toDateTime = (g) => {
  const [y, m, d] = g.date.split("-").map(Number);
  let hh = 23;
  let mm = 59;
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(g.time || "");
  if (match) {
    hh = Number(match[1]) % 12;
    mm = Number(match[2]);
    if (/pm/i.test(match[3])) hh += 12;
  }
  return new Date(y, m - 1, d, hh, mm);
};

const formatDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const TypeBadge = ({ type }) => {
  const map = {
    Home: "text-emerald-400 border-emerald-400/40",
    Away: "text-sky-400 border-sky-400/40",
    Neutral: "text-amber-400 border-amber-400/40",
  };
  return (
    <span
      data-testid={`badge-type-${type.toLowerCase()}`}
      className={`inline-flex items-center border px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-bold ${
        map[type] || "text-zinc-400 border-zinc-700"
      }`}
    >
      {type}
    </span>
  );
};

const GameRow = ({ g, upcoming }) => {
  const cityState = [g.city, g.state].filter(Boolean).join(", ");
  return (
    <div
      data-testid="schedule-game-row"
      className={`grid grid-cols-12 gap-2 md:gap-4 items-center py-3 ${
        upcoming ? "" : "opacity-45"
      }`}
    >
      {/* Date + time */}
      <div className="col-span-4 md:col-span-3">
        <div className="text-white text-xs md:text-sm font-bold uppercase tracking-wide">
          {formatDate(g.date)}
        </div>
        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-zinc-500">
          <Clock className="h-3 w-3" />
          {g.time}
        </div>
      </div>

      {/* Opponent + venue */}
      <div className="col-span-8 md:col-span-6">
        <div className="text-white text-sm md:text-base font-semibold leading-snug">
          {g.tournament ? (
            <>vs. {g.opponent}</>
          ) : (
            <>
              {g.type === "Home" ? "vs. " : g.type === "Away" ? "at " : "vs. "}
              {g.opponent}
            </>
          )}
        </div>
        {g.tournament && (
          <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#FF3B30] mt-0.5">
            {g.tournament}
          </div>
        )}
        <div className="mt-1 flex items-center gap-1.5 text-[11px] text-zinc-500">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="truncate">
            {g.venue}
            {cityState ? ` · ${cityState}` : g.tba ? "" : " · TBA"}
          </span>
        </div>
      </div>

      {/* Badges */}
      <div className="col-span-12 md:col-span-3 flex flex-wrap gap-1.5 md:justify-end">
        <TypeBadge type={g.type} />
        {g.conference && (
          <span
            data-testid="badge-conference"
            className="inline-flex items-center border border-[#FF3B30]/50 text-[#FF3B30] px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-bold"
          >
            Conf
          </span>
        )}
        {g.tournament && (
          <span
            data-testid="badge-tournament"
            className="inline-flex items-center border border-violet-400/40 text-violet-300 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-bold"
          >
            Tourney
          </span>
        )}
      </div>
    </div>
  );
};

export const SeniorSchedule = () => {
  const [showAll, setShowAll] = useState(false);

  // Expand full schedule when printing, restore afterward.
  useEffect(() => {
    const onBefore = () => setShowAll(true);
    window.addEventListener("beforeprint", onBefore);
    return () => window.removeEventListener("beforeprint", onBefore);
  }, []);

  const now = new Date();
  const withDt = seniorSchedule
    .map((g) => ({ ...g, dt: toDateTime(g) }))
    .sort((a, b) => a.dt - b.dt);
  const upcoming = withDt.filter((g) => g.dt >= now);
  const next = upcoming[0] || null;
  const nextFive = upcoming.slice(1, 6);

  const nextCityState = next
    ? [next.city, next.state].filter(Boolean).join(", ")
    : "";

  return (
    <div
      data-testid="senior-schedule"
      className="md:ml-[16.6667%] border-l-2 border-[#FF3B30] pl-5 md:pl-6 py-3 bg-[#0a0a0a]"
    >
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 mb-3 flex items-center gap-2">
        <Calendar className="h-3.5 w-3.5 text-[#FF3B30]" />
        Game Schedule
      </div>

      {/* Next game — prominent */}
      {next && (
        <div
          data-testid="schedule-next-game"
          className="border border-[#FF3B30]/40 bg-[#141414] p-4 md:p-5 mb-5"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF3B30] mb-2">
            <Star className="h-3.5 w-3.5 fill-[#FF3B30]" />
            Next Game
          </div>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-display text-2xl md:text-3xl text-white uppercase leading-none">
                {next.type === "Away" ? "at " : "vs. "}
                {next.opponent}
              </div>
              {next.tournament && (
                <div className="text-xs uppercase tracking-[0.14em] font-bold text-[#FF3B30] mt-1">
                  {next.tournament}
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-400">
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatDate(next.date)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  {next.time}
                </span>
                <span className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" />
                  {next.venue}
                  {nextCityState ? ` · ${nextCityState}` : ""}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <TypeBadge type={next.type} />
              {next.conference && (
                <span className="inline-flex items-center border border-[#FF3B30]/50 text-[#FF3B30] px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-bold">
                  Conf
                </span>
              )}
              {next.tournament && (
                <span className="inline-flex items-center border border-violet-400/40 text-violet-300 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-bold">
                  Tourney
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Next few games */}
      {!showAll && nextFive.length > 0 && (
        <div className="divide-y divide-zinc-900" data-testid="schedule-next-list">
          {nextFive.map((g, i) => (
            <GameRow key={`up-${i}`} g={g} upcoming />
          ))}
        </div>
      )}

      {/* Full schedule */}
      {showAll && (
        <div
          className="divide-y divide-zinc-900"
          data-testid="schedule-full-list"
        >
          {withDt.map((g, i) => (
            <GameRow key={`all-${i}`} g={g} upcoming={g.dt >= now} />
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.16em] font-semibold text-zinc-600">
        <span className="text-emerald-400/80">Home</span>
        <span className="text-sky-400/80">Away</span>
        <span className="text-amber-400/80">Neutral</span>
        <span className="text-[#FF3B30]/80">Conf = Conference</span>
        <span className="text-violet-300/80">Tourney = Tournament</span>
      </div>

      <button
        type="button"
        data-testid="schedule-view-full-btn"
        onClick={() => setShowAll((v) => !v)}
        className="no-print mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-white hover:text-[#FF3B30] transition-colors"
      >
        {showAll ? "Show Less" : "View Full Schedule"}
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-200 ${
            showAll ? "rotate-180" : ""
          }`}
        />
      </button>
    </div>
  );
};
